/* eslint-disable */

import axios from "axios";
import { API_CONFIG, API_ENDPOINTS } from "../../../config/api";

// Base adapter interface
export interface ChatAdapter {
    startSession(context: any): Promise<SessionStartResponse>;
    sendMessage(sessionId: string, message: string, context?: any): Promise<MessageResponse>;
    processResponse(response: any): ProcessedResponse;
    getInitialState(): any;
    getApiConfig(): ApiConfig;
}

export interface SessionStartResponse {
    sessionId: string;
    initialMessage: string;
    initialState?: any;
}

export interface MessageResponse {
    message: string;
    data?: any;
    action?: string;
}

export interface ProcessedResponse {
    message: string;
    action?: string;
    data?: any;
}

export interface ApiConfig {
    baseURL: string;
    startEndpoint: string;
    messageEndpoint: string;
}

// AI Jobs Session Adapter - uses Xano AI session management with job extraction logic
export class AiJobsSessionAdapter implements ChatAdapter {
    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.AI_URL,
            startEndpoint: API_ENDPOINTS.JOB_COACH.START,
            messageEndpoint: API_ENDPOINTS.JOB_COACH.MESSAGE
        };
    }

    async startSession(): Promise<SessionStartResponse> {
        try {
            const { startXanoAiJobSearchSession } = await import('../../../services/api');
            const response = await startXanoAiJobSearchSession();

            return {
                sessionId: response.session_id,
                initialMessage: response.message,
                initialState: {
                    jobRecommendations: [],
                    trainingSuggestions: null,
                    matchedJobs: [],
                    showJobsList: false,
                    hasStoredJobs: false,
                    isComplete: false,
                    suggestedActions: []
                }
            };
        } catch (error) {
            console.error("Failed to start AI Jobs session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            const { sendXanoAiJobSearchMessage } = await import('../../../services/api');
            const response = await sendXanoAiJobSearchMessage(sessionId, message);

            // Check if response is actually an error (HTTP 200 but error payload)
            if (response?.payload === "Something went wrong" || response?.statement === "Throw Error") {
                // Pass through the error indicators
                return {
                    message: response.message,
                    data: {
                        isTimeout: true,
                        errorPayload: response.payload,
                        errorStatement: response.statement
                    }
                };
            }

            return {
                message: response.message,
                data: {
                    context: response.context,
                    isComplete: response.isComplete,
                    suggestedActions: response.suggestedActions || [],
                    matchedJobs: response.matchedJobs,
                    readyForJobSearch: response.readyForJobSearch
                }
            };
        } catch (error: any) {
            console.error("Failed to send message:", error);

            // Handle 500-series HTTP errors (502, 500, 503, 504, etc.)
            const status = error?.response?.status;
            if (status >= 500 && status < 600) {
                // Treat as timeout/processing error - jobs are likely ready
                return {
                    message: '',
                    data: {
                        isTimeout: true,
                        errorStatus: status
                    }
                };
            }

            // Re-throw other errors (4xx, network errors, etc.)
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        const result: ProcessedResponse = {
            message: response.message
        };

        // Handle readyForJobSearch flag - trigger pinecone jobs fetch
        if (response.data?.readyForJobSearch) {
            result.action = 'FETCH_PINECONE_JOBS';
            result.data = {
                readyForJobSearch: true
            };
        }

        // Handle job results
        if (response.data?.matchedJobs && response.data.matchedJobs.length > 0) {
            result.action = 'SHOW_JOBS_LIST';
            result.data = {
                jobs: response.data.matchedJobs.map(this.extractJobInfo)
            };
        }

        // Handle other actions
        if (response.data?.context?.recommendations?.length > 0) {
            result.action = 'SHOW_RECOMMENDATIONS';
            result.data = {
                ...result.data,
                recommendations: response.data.context.recommendations
            };
        }

        if (response.data?.isComplete) {
            result.action = 'SESSION_COMPLETE';
        }

        if (response.data?.suggestedActions?.length > 0) {
            result.data = {
                ...result.data,
                suggestedActions: response.data.suggestedActions
            };
        }

        return result;
    }

    getInitialState() {
        return {
            jobRecommendations: [],
            trainingSuggestions: null,
            matchedJobs: [],
            showJobsList: false,
            hasStoredJobs: false,
            isComplete: false,
            suggestedActions: [],
            readyForJobSearch: false
        };
    }

    // Transform JobListing from pinecone API to JobsListComponent format
    public transformPineconeJob = (job: any) => {
        const text = job.metadata?.text || job.text || '';
        // Extract title from text
        let title = job.metadata?.title || 'Job Position';
        if (title.length > 100) {
            const titleMatch = text.match(/^([^.\n]{10,80})/);
            title = titleMatch ? titleMatch[1].trim() : title.substring(0, 80) + '...';
        }

        // Extract company name
        let company = 'Company';
        if (text) {
            const companyPatterns = [
                /(?:bei|für|von)\s+([A-Z][a-zA-Z\s&.-]+?)(?:\s-|\n|$)/i,
                /([A-Z][a-zA-Z\s&.-]+?)\s*-\s*(?:Ihr|unser|der)/i,
                /^([A-Z][a-zA-Z\s&.-]+?)\s*(?:sucht|bietet)/i
            ];

            for (const pattern of companyPatterns) {
                const match = text.match(pattern);
                if (match && match[1]) {
                    company = match[1].trim();
                    break;
                }
            }
        }

        // Safely handle description with fallback
        const fullDescription = job.metadata?.description || '';
        // Truncate for display only
        const description = fullDescription.length > 300
            ? fullDescription.substring(0, 300) + '...'
            : fullDescription;

        return {
            id: job.job_id || job.id,
            title,
            description, // Truncated for UI display
            fullDescription, // Full description for cover letter generation
            company,
            location: job.metadata?.location || job.metadata?.city || 'Location',
            matchScore: job.score ? Math.round(job.score * 100) : 0,
            link: job.metadata?.link || '#'
        };
    };

    // Preserve existing job extraction logic from original AiJobsAdapter
    private extractJobInfo = (job: any) => {
        const text = job.metadata?.text || job.description || '';
        const lines = text.split('\n').filter((line: string) => line.trim());

        // Extract title (preserve existing logic)
        let title = lines[0]?.trim() || job.title || 'Job Position';
        if (title.length > 100) {
            const titleMatch = text.match(/^([^.\n]{10,80})/);
            title = titleMatch ? titleMatch[1].trim() : title.substring(0, 80) + '...';
        }

        // Extract company name (preserve existing logic)
        let company = job.company || 'Company';
        if (!job.company && text) {
            const companyPatterns = [
                /(?:bei|für|von)\s+([A-Z][a-zA-Z\s&.-]+?)(?:\s-|\n|$)/i,
                /([A-Z][a-zA-Z\s&.-]+?)\s*-\s*(?:Ihr|unser|der)/i,
                /^([A-Z][a-zA-Z\s&.-]+?)\s*(?:sucht|bietet)/i
            ];

            for (const pattern of companyPatterns) {
                const match = text.match(pattern);
                if (match && match[1]) {
                    company = match[1].trim();
                    break;
                }
            }
        }

        return {
            id: job.id,
            title,
            description: text.length > 300 ? text.substring(0, 300) + '...' : text,
            company,
            location: job.metadata?.location || job.metadata?.city || job.location,
            matchScore: job.score ? Math.round(job.score * 100) : job.matchScore,
            link: job.metadata?.link || job.link
        };
    };

    async getSessions(): Promise<any[]> {
        try {
            const { getXanoAiJobSearchSessions } = await import('../../../services/api');
            return await getXanoAiJobSearchSessions();
        } catch (error) {
            console.error("Failed to get sessions:", error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<any[]> {
        try {
            const { getXanoAiJobSearchSessionMessages } = await import('../../../services/api');
            return await getXanoAiJobSearchSessionMessages(sessionId);
        } catch (error) {
            console.error("Failed to get session messages:", error);
            throw error;
        }
    }
}

// Cover Letter Adapter - preserves all existing CoverLetterChat logic
export class CoverLetterAdapter implements ChatAdapter {
    private api = axios.create({
        baseURL: API_CONFIG.BASE_URL,
        timeout: API_CONFIG.TIMEOUT
    });

    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.BASE_URL,
            startEndpoint: API_ENDPOINTS.COVER_LETTER.START,
            messageEndpoint: API_ENDPOINTS.COVER_LETTER.CONTINUE
        };
    }

    async startSession(context: { userProfile: any, jobDescription?: string }): Promise<SessionStartResponse> {
        try {
            const {
                candidate_matching_positions,
                id,
                key,
                new_positions_auto_id,
                position_pattern_id,
                positions_auto_id,
                potential_jobs,
                potential_jobs_id,
                registered_at,
                ...cleanProfile
            } = context.userProfile;

            const response = await this.api.post(API_ENDPOINTS.COVER_LETTER.START, {
                userProfile: cleanProfile,
                jobDescription: context.jobDescription ?? ""
            });

            return {
                sessionId: response.data.sessionId,
                initialMessage: response.data.message,
            };
        } catch (error) {
            console.error("Failed to start chat session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            // Use exact existing logic from CoverLetterChat.tsx
            const response = await this.api.post(
                API_ENDPOINTS.COVER_LETTER.CONTINUE,
                { answer: message, sessionId }
            );

            return {
                message: response.data.message
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        return {
            message: response.message
        };
    }

    getInitialState() {
        return {};
    }
}

// Interview Adapter - preserves all existing InterviewChat logic
export class InterviewAdapter implements ChatAdapter {
    private api = axios.create({
        baseURL: API_CONFIG.BASE_URL,
        timeout: API_CONFIG.TIMEOUT
    });

    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.BASE_URL,
            startEndpoint: API_ENDPOINTS.INTERVIEW.START,
            messageEndpoint: API_ENDPOINTS.INTERVIEW.CONTINUE
        };
    }

    async startSession(context: { userProfile: any, jobRole?: string }): Promise<SessionStartResponse> {
        try {
            // Use exact existing logic from InterviwChat.tsx
            const {
                candidate_matching_positions,
                id,
                key,
                new_positions_auto_id,
                position_pattern_id,
                positions_auto_id,
                potential_jobs,
                potential_jobs_id,
                registered_at,
                ...cleanProfile
            } = context.userProfile;

            const response = await this.api.post(API_ENDPOINTS.INTERVIEW.START, {
                userProfile: cleanProfile,
                jobRole: context.jobRole || undefined
            });

            return {
                sessionId: response.data.sessionId,
                initialMessage: response.data.message,
                initialState: {
                    stage: response.data.stage
                }
            };
        } catch (error) {
            console.error("Failed to start interview session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            // Use exact existing logic from InterviwChat.tsx
            const response = await this.api.post(
                API_ENDPOINTS.INTERVIEW.CONTINUE,
                { answer: message, sessionId }
            );

            return {
                message: response.data.message,
                data: { stage: response.data.stage }
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        return {
            message: response.message,
            data: response.data
        };
    }

    getInitialState() {
        return {
            stage: undefined
        };
    }
}

export class InterviewXanoAdapter implements ChatAdapter {
    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.AI_URL,
            startEndpoint: API_ENDPOINTS.JOB_COACH.START,
            messageEndpoint: API_ENDPOINTS.JOB_COACH.MESSAGE
        };
    }

    async startSession(context?: { jobRole?: string }): Promise<SessionStartResponse> {
        try {
            const { startXanoAiInterviewSession } = await import('../../../services/api');
            const response = await startXanoAiInterviewSession(context?.jobRole || null);

            return {
                sessionId: response.session_id,
                initialMessage: response.message,
                initialState: {
                    isComplete: false,
                    suggestedActions: []
                }
            };
        } catch (error) {
            console.error("Failed to start interview session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            const { sendXanoAiInterviewMessage } = await import('../../../services/api');
            const response = await sendXanoAiInterviewMessage(sessionId, message);

            return {
                message: response.message,
                data: {
                    context: response.context,
                    isComplete: response.isComplete,
                    suggestedActions: response.suggestedActions || []
                }
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        const result: ProcessedResponse = {
            message: response.message
        };

        if (response.data?.context?.recommendations?.length > 0) {
            result.action = 'SHOW_RECOMMENDATIONS';
            result.data = { recommendations: response.data.context.recommendations };
        }

        if (response.data?.isComplete) {
            result.action = 'SESSION_COMPLETE';
        }

        if (response.data?.suggestedActions?.length > 0) {
            result.data = {
                ...result.data,
                suggestedActions: response.data.suggestedActions
            };
        }

        return result;
    }

    getInitialState() {
        return {
            isComplete: false,
            suggestedActions: []
        };
    }

    async getSessions(): Promise<any[]> {
        try {
            const { getXanoAiInterviewSessions } = await import('../../../services/api');
            return await getXanoAiInterviewSessions();
        } catch (error) {
            console.error("Failed to get sessions:", error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<any[]> {
        try {
            const { getXanoAiInterviewSessionMessages } = await import('../../../services/api');
            return await getXanoAiInterviewSessionMessages(sessionId);
        } catch (error) {
            console.error("Failed to get session messages:", error);
            throw error;
        }
    }
}

// Cover Letter Xano Adapter - uses same API as Interview/JobCoach
export class CoverLetterXanoAdapter implements ChatAdapter {
    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.AI_URL,
            startEndpoint: API_ENDPOINTS.JOB_COACH.START,
            messageEndpoint: API_ENDPOINTS.JOB_COACH.MESSAGE
        };
    }

    async startSession(context?: { jobRole?: string, job_demand_id?: number }): Promise<SessionStartResponse> {
        try {
            const { startXanoAiCoverLetterSession } = await import('../../../services/api');
            const response = await startXanoAiCoverLetterSession(context?.jobRole || null, context?.job_demand_id);

            return {
                sessionId: response.session_id,
                initialMessage: response.message,
                initialState: {
                    isComplete: false,
                    suggestedActions: []
                }
            };
        } catch (error) {
            console.error("Failed to start cover letter session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            const { sendXanoAiCoverLetterMessage } = await import('../../../services/api');
            const response = await sendXanoAiCoverLetterMessage(sessionId, message);

            return {
                message: response.message,
                data: {
                    context: response.context,
                    isComplete: response.isComplete,
                    suggestedActions: response.suggestedActions || []
                }
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        const result: ProcessedResponse = {
            message: response.message
        };

        if (response.data?.context?.recommendations?.length > 0) {
            result.action = 'SHOW_RECOMMENDATIONS';
            result.data = { recommendations: response.data.context.recommendations };
        }

        if (response.data?.isComplete) {
            result.action = 'SESSION_COMPLETE';
        }

        if (response.data?.suggestedActions?.length > 0) {
            result.data = {
                ...result.data,
                suggestedActions: response.data.suggestedActions
            };
        }

        return result;
    }

    getInitialState() {
        return {
            isComplete: false,
            suggestedActions: []
        };
    }

    async getSessions(): Promise<any[]> {
        try {
            const { getXanoAiCoverLetterSessions } = await import('../../../services/api');
            return await getXanoAiCoverLetterSessions();
        } catch (error) {
            console.error("Failed to get sessions:", error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<any[]> {
        try {
            const { getXanoAiCoverLetterSessionMessages } = await import('../../../services/api');
            return await getXanoAiCoverLetterSessionMessages(sessionId);
        } catch (error) {
            console.error("Failed to get session messages:", error);
            throw error;
        }
    }
}

// Job Coach Adapter - preserves all existing JobCoachChat logic
export class JobCoachAdapter implements ChatAdapter {
    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.AI_URL,
            startEndpoint: API_ENDPOINTS.JOB_COACH.START,
            messageEndpoint: API_ENDPOINTS.JOB_COACH.MESSAGE
        };
    }

    async startSession(): Promise<SessionStartResponse> {
        try {
            const { startXanoAiSession } = await import('../../../services/api');
            const response = await startXanoAiSession();

            return {
                sessionId: response.session_id,
                initialMessage: response.message,
                initialState: {
                    isComplete: false,
                    suggestedActions: []
                }
            };
        } catch (error) {
            console.error("Failed to start job coach session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            const { sendXanoAiMessage } = await import('../../../services/api');
            const response = await sendXanoAiMessage(sessionId, message);

            return {
                message: response.message,
                data: {
                    context: response.context,
                    isComplete: response.isComplete,
                    suggestedActions: response.suggestedActions || []
                }
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        const result: ProcessedResponse = {
            message: response.message
        };

        if (response.data?.context?.recommendations?.length > 0) {
            result.action = 'SHOW_RECOMMENDATIONS';
            result.data = { recommendations: response.data.context.recommendations };
        }

        if (response.data?.isComplete) {
            result.action = 'SESSION_COMPLETE';
        }

        if (response.data?.suggestedActions?.length > 0) {
            result.data = {
                ...result.data,
                suggestedActions: response.data.suggestedActions
            };
        }

        return result;
    }

    getInitialState() {
        return {
            isComplete: false,
            suggestedActions: []
        };
    }

    async getSessions(): Promise<any[]> {
        try {
            const { getXanoAiSessions } = await import('../../../services/api');
            return await getXanoAiSessions();
        } catch (error) {
            console.error("Failed to get sessions:", error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<any[]> {
        try {
            const { getXanoAiSessionMessages } = await import('../../../services/api');
            return await getXanoAiSessionMessages(sessionId);
        } catch (error) {
            console.error("Failed to get session messages:", error);
            throw error;
        }
    }
}

// Rentensteuerberater (Pension Tax Advisor) Adapter
export class RentensteuerberaterAdapter implements ChatAdapter {
    getApiConfig(): ApiConfig {
        return {
            baseURL: API_CONFIG.AI_URL,
            startEndpoint: API_ENDPOINTS.JOB_COACH.START,
            messageEndpoint: API_ENDPOINTS.JOB_COACH.MESSAGE
        };
    }

    async startSession(): Promise<SessionStartResponse> {
        try {
            const { startXanoAiRentensteuerberaterSession } = await import('../../../services/api');
            const response = await startXanoAiRentensteuerberaterSession();

            return {
                sessionId: response.session_id,
                initialMessage: response.message,
                initialState: {
                    isComplete: false,
                    suggestedActions: []
                }
            };
        } catch (error) {
            console.error("Failed to start Steuer & Rente Experte session:", error);
            throw error;
        }
    }

    async sendMessage(sessionId: string, message: string): Promise<MessageResponse> {
        try {
            const { sendXanoAiRentensteuerberaterMessage } = await import('../../../services/api');
            const response = await sendXanoAiRentensteuerberaterMessage(sessionId, message);

            return {
                message: response.message,
                data: {
                    context: response.context,
                    isComplete: response.isComplete,
                    suggestedActions: response.suggestedActions || []
                }
            };
        } catch (error) {
            console.error("Failed to send message:", error);
            throw error;
        }
    }

    processResponse(response: MessageResponse): ProcessedResponse {
        const result: ProcessedResponse = {
            message: response.message
        };

        if (response.data?.context?.recommendations?.length > 0) {
            result.action = 'SHOW_RECOMMENDATIONS';
            result.data = { recommendations: response.data.context.recommendations };
        }

        if (response.data?.isComplete) {
            result.action = 'SESSION_COMPLETE';
        }

        if (response.data?.suggestedActions?.length > 0) {
            result.data = {
                ...result.data,
                suggestedActions: response.data.suggestedActions
            };
        }

        return result;
    }

    getInitialState() {
        return {
            isComplete: false,
            suggestedActions: []
        };
    }

    async getSessions(): Promise<any[]> {
        try {
            const { getXanoAiRentensteuerberaterSessions } = await import('../../../services/api');
            return await getXanoAiRentensteuerberaterSessions();
        } catch (error) {
            console.error("Failed to get sessions:", error);
            throw error;
        }
    }

    async getSessionMessages(sessionId: string): Promise<any[]> {
        try {
            const { getXanoAiRentensteuerberaterSessionMessages } = await import('../../../services/api');
            return await getXanoAiRentensteuerberaterSessionMessages(sessionId);
        } catch (error) {
            console.error("Failed to get session messages:", error);
            throw error;
        }
    }
}

// Adapter factory
export const createAdapter = (type: 'aiJobs' | 'coverLetter' | 'interview' | 'jobCoach' | 'rentensteuerberater'): ChatAdapter => {
    switch (type) {
        case 'aiJobs':
            return new AiJobsSessionAdapter();
        case 'coverLetter':
            return new CoverLetterXanoAdapter();
        case 'interview':
            return new InterviewXanoAdapter();
        case 'jobCoach':
            return new JobCoachAdapter();
        case 'rentensteuerberater':
            return new RentensteuerberaterAdapter();
        default:
            throw new Error(`Unknown adapter type: ${type}`);
    }
};
